/**
 * VistoeraTASK-00637 — Workspace override draft helpers (INHERIT/ALLOW/DENY).
 * Separate from plan ALLOW/DENY helpers so PlanAuthMatrixPage stays unchanged.
 */
import { grantKey, parseGrantKey, type AuthDecision, type GrantKey, type MatrixRow } from './authMatrixDraft'

export type WorkspaceDecision = 'INHERIT' | 'ALLOW' | 'DENY'

export function normalizeWorkspaceDecision(decision: string | null | undefined): WorkspaceDecision {
  if (decision === 'ALLOW' || decision === 'DENY') return decision
  return 'INHERIT'
}

export function planDecisionLabel(planDecision: string | null | undefined): '允许' | '禁止' {
  return planDecision === 'ALLOW' ? '允许' : '禁止'
}

export function workspaceDecisionLabel(d: WorkspaceDecision): string {
  if (d === 'ALLOW') return '允许'
  if (d === 'DENY') return '禁止'
  return '继承'
}

export function effectiveAllowedLabel(allowed: boolean | undefined): string {
  if (allowed === true) return '允许'
  if (allowed === false) return '禁止'
  return '—'
}

export function mapsFromWorkspaceRows(rows: MatrixRow[]): {
  baseline: Record<string, WorkspaceDecision>
  draft: Record<string, WorkspaceDecision>
  planDefaults: Record<string, string | null>
  effectiveAllowed: Record<string, boolean>
  nodeIds: string[]
  channelIds: string[]
} {
  const baseline: Record<string, WorkspaceDecision> = {}
  const draft: Record<string, WorkspaceDecision> = {}
  const planDefaults: Record<string, string | null> = {}
  const effectiveAllowed: Record<string, boolean> = {}
  const nodeIds: string[] = []
  const channelIds: string[] = []
  const seenChannels = new Set<string>()

  for (const row of rows) {
    const nid = row.node.subject_id
    nodeIds.push(nid)
    const nk = grantKey('node', nid)
    const nd = normalizeWorkspaceDecision(row.node.decision)
    baseline[nk] = nd
    draft[nk] = nd
    planDefaults[nk] = row.node.plan_decision ?? null
    effectiveAllowed[nk] = Boolean(row.node.effective_allowed)

    for (const ch of row.channels || []) {
      const ck = grantKey('channel', ch.subject_id)
      const cd = normalizeWorkspaceDecision(ch.decision)
      baseline[ck] = cd
      draft[ck] = cd
      planDefaults[ck] = ch.plan_decision ?? null
      effectiveAllowed[ck] = Boolean(ch.effective_allowed)
      if (!seenChannels.has(ch.subject_id)) {
        seenChannels.add(ch.subject_id)
        channelIds.push(ch.subject_id)
      }
    }
  }

  return { baseline, draft, planDefaults, effectiveAllowed, nodeIds, channelIds }
}

export function isWorkspaceDirty(
  baseline: Record<string, WorkspaceDecision>,
  draft: Record<string, WorkspaceDecision>,
): boolean {
  for (const [key, value] of Object.entries(draft)) {
    if ((baseline[key] ?? 'INHERIT') !== value) return true
  }
  for (const key of Object.keys(baseline)) {
    if (!(key in draft) && baseline[key] !== 'INHERIT') return true
  }
  return false
}

export function workspaceDirtyEntries(
  baseline: Record<string, WorkspaceDecision>,
  draft: Record<string, WorkspaceDecision>,
): Array<{ kind: 'node' | 'channel'; subjectId: string; decision: WorkspaceDecision }> {
  const out: Array<{ kind: 'node' | 'channel'; subjectId: string; decision: WorkspaceDecision }> = []
  const keys = new Set([...Object.keys(baseline), ...Object.keys(draft)])
  for (const key of keys) {
    const next = draft[key] ?? 'INHERIT'
    const prev = baseline[key] ?? 'INHERIT'
    if (next === prev) continue
    const { kind, subjectId } = parseGrantKey(key as GrantKey)
    out.push({ kind, subjectId, decision: next })
  }
  return out
}

/** Count non-INHERIT overrides in draft (租户覆盖数). */
export function countWorkspaceOverrides(
  draft: Record<string, WorkspaceDecision>,
  nodeIds: string[],
  channelIds: string[],
): { nodes: number; channels: number } {
  let nodes = 0
  let channels = 0
  for (const id of nodeIds) {
    if (draft[grantKey('node', id)] !== 'INHERIT') nodes += 1
  }
  for (const id of channelIds) {
    if (draft[grantKey('channel', id)] !== 'INHERIT') channels += 1
  }
  return { nodes, channels }
}

/** Reset draft: every subject → INHERIT (套餐默认). */
export function draftAllInherit(
  nodeIds: string[],
  channelIds: string[],
): Record<string, WorkspaceDecision> {
  const draft: Record<string, WorkspaceDecision> = {}
  for (const id of nodeIds) draft[grantKey('node', id)] = 'INHERIT'
  for (const id of channelIds) draft[grantKey('channel', id)] = 'INHERIT'
  return draft
}

export type { AuthDecision, GrantKey }
